import { METER_FILL, sellThroughLevel } from '@/lib/sellThrough'

// Colour rides on a small meter; the number stays in ink (never colour-alone).
// The meter follows the percentage only; stockouts are shown separately by
// SoldOutPill in the Missed rev. column.
export function SellThrough({ pct }: { pct: number | null }) {
  if (pct == null) return <span className="text-[var(--mute)]">—</span>
  const level = sellThroughLevel(pct)
  return (
    <span className="inline-flex items-center gap-1 tabular-nums">
      <span
        className="inline-block h-1 w-9 overflow-hidden rounded-full bg-[var(--meter-track)]"
        data-testid="sell-through-meter"
        data-level={level}
        aria-hidden
      >
        <span
          className={`block h-full rounded-full ${METER_FILL[level]}`}
          style={{ width: `${Math.min(Math.max(pct, 0), 100)}%` }}
        />
      </span>
      {pct.toFixed(1)}%
    </span>
  )
}

// Amber "attention" pill (bring more next time). Without a count it marks a
// single variant; with a count it rolls up variants at product/category level.
export function SoldOutPill({ count }: { count?: number }) {
  const label =
    count == null
      ? 'Sold out: stocked out during the event'
      : `${count} ${count === 1 ? 'variant' : 'variants'} sold out during the event`
  return (
    <span
      className="inline-block rounded-full text-[10.5px] font-semibold px-2 py-0.5 leading-none whitespace-nowrap bg-[var(--status-awaiting-bg)] text-[var(--status-awaiting-fg)]"
      title={count == null ? 'Stocked out' : label}
      aria-label={label}
    >
      {count == null ? '⚠ Sold out' : `⚠ ${count} sold out`}
    </span>
  )
}

// A row's margin is highlighted (green, never red/amber) when it beats the
// page's overall margin under the current filters by 10 points or more.
export function MarginCell({ pct, avg }: { pct: number | null; avg: number | null }) {
  if (pct == null) return <span className="text-[var(--mute)]">—</span>
  const strong = avg != null && pct >= avg + 10
  return (
    <span className={strong ? 'text-[var(--status-approved-fg)] font-medium' : undefined}>{pct}%</span>
  )
}
